import { useCallback } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import config from 'constants/apiConfig';

import type { FieldDraftCitation } from './types';

export type AiFieldProvenanceRow = {
  fieldKey: string;
  sources: FieldDraftCitation[];
  acceptedAt?: string;
};

type PersistArgs = {
  incidentId: string;
  targetKind: 'incident_info' | 'sitrep_section';
  targetId: string;
  fields: Array<{ fieldKey: string; sources: FieldDraftCitation[] }>;
};

export const aiFieldProvenanceKeys = {
  all: ['aiFieldProvenance'] as const,
  byTarget: (targetKind: string, targetId: string) =>
    [...aiFieldProvenanceKeys.all, targetKind, targetId] as const,
};

export const useAiFieldProvenance = (
  targetKind: 'incident_info' | 'sitrep_section',
  targetId: string | undefined
) =>
  useQuery({
    queryKey: aiFieldProvenanceKeys.byTarget(targetKind, targetId ?? ''),
    enabled: !!targetId,
    queryFn: async () => {
      const { data } = await axios.get<{ rows: AiFieldProvenanceRow[] }>(
        config.apiGateway.aiFieldProvenance,
        { params: { targetKind, targetId } }
      );
      return data.rows ?? [];
    },
  });

export const usePersistAiFieldProvenance = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (args: PersistArgs) => {
      if (args.fields.length === 0) return;
      await axios.post(config.apiGateway.aiFieldProvenance, args);
      await queryClient.invalidateQueries({
        queryKey: aiFieldProvenanceKeys.byTarget(args.targetKind, args.targetId),
      });
    },
    [queryClient]
  );
};
